import { useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "components/modal";
import { Input } from "components/website/form/input";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { CloseCircleGreyIcon } from "assets/website";
import { useWaitlist } from "hooks";
import { toast } from 'sonner';

interface JoinWaitlistModalProps {
  show: boolean;
  onClose: () => void;
}

interface WaitlistFormData {
  fullName: string;
  email: string;
  jobRole: string;
}

const JoinWaitlistModal: React.FC<JoinWaitlistModalProps> = ({ show, onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+234");
  const [submitted, setSubmitted] = useState(false);

  const { mutate: joinWaitlist, isPending: loading } = useWaitlist();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WaitlistFormData>();

  const handlePhoneChange = (phone: string) => {
    setPhoneNumber(phone);
  };

  const onSubmit = async (data: WaitlistFormData) => {
    setSubmitted(true);

    const hasFormErrors = Object.keys(errors).length > 0;
    const hasPhoneError = !phoneNumber || !countryCode;

    if (hasFormErrors || hasPhoneError) {
      if (hasFormErrors) {
        toast.error('Please fill in all required fields correctly.');
      }
      if (hasPhoneError) {
        toast.error('Phone number is required.');
      }
      return;
    }

    const dialCode = countryCode.replace('+', '');
    const phoneWithoutCode = phoneNumber.startsWith(dialCode)
      ? phoneNumber.slice(dialCode.length)
      : phoneNumber;

    joinWaitlist(
      {
        full_name: data.fullName,
        email: data.email,
        job_role: data.jobRole,
        country_code: countryCode,
        phone_number: phoneWithoutCode,
      },
      {
        onSuccess: () => {
          reset();
          setPhoneNumber("");
          setCountryCode("+234");
          setSubmitted(false);
          onClose();
        },
      }
    );
  };

  return (
    <Modal show={show} close={onClose} variant="website">
      <div className="p-4 md:p-6 max-w-[450px] relative">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#012169] font-display">
            Join Waitlist
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors absolute top-0 right-0 text-gray-500 hover:text-gray-700"
          >
            <CloseCircleGreyIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
          <Input
            type="text"
            name="fullName"
            register={register}
            required
            placeholder="Enter your full name"
            label="Full Name"
            validatorMessage={errors.fullName ? "Full name is required" : undefined}
            parentClassName="!mb-4"
          />

          <Input
            type="email"
            name="email"
            register={register}
            required
            placeholder="Enter your email address"
            label="Email address"
            validatorMessage={errors.email ? "Email is required" : undefined}
            parentClassName="!mb-4"
          />

          <Input
            type="text"
            name="jobRole"
            register={register}
            required
            placeholder="Enter your job role"
            label="Job Role"
            validatorMessage={errors.jobRole ? "Job role is required" : undefined}
            parentClassName="!mb-4"
          />

          <div className="!mb-4">
            <label className="block text-sm text-[#101323] font-medium mb-2">
              Phone Number
            </label>
            <PhoneInput
              defaultCountry="ng"
              value={phoneNumber}
              onChange={handlePhoneChange}
              inputClassName="!w-full !h-[44px] !pr-4 !pl-3 !border !border-[#dae0e6] !border-l-0 !rounded-r-md focus:!outline-none focus:!ring-0 focus:!border-[#dae0e6] !transition-colors !text-neutral-900 !text-sm placeholder:!text-neutral-400"
              countrySelectorStyleProps={{
                buttonClassName: "!h-[44px] !pl-3 !pr-2 !border !border-[#dae0e6] !border-r-0 !rounded-l-md !bg-white",
              }}
            />
            {submitted && !phoneNumber && (
              <small className="text-xs text-[#e74444] block mt-2">Phone number is required</small>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#012169] hover:bg-[#011550] disabled:bg-[#011550]/70 text-white font-semibold rounded-lg transition-colors mt-4"
          >
            {loading ? "Joining..." : "Join"}
          </button>
        </form>
      </div>
    </Modal>
  );
};

export { JoinWaitlistModal };
